import { createContext, ReactNode, Reducer, useCallback, useContext, useEffect, useReducer, useRef } from "react";
import { contactsApi } from "../../redux/services/contactsApi";
import { useTypedSelector } from "../../redux/hooks";
import { selectCurrentUserId } from "../../redux/slices";
import ModalFormContainer from "../../components/forms/modalFormContainer/ModalFormContainer";
import formStyles from "../../sass/globalModules/forms.module.scss"
import styles from "./styles.module.scss";
import { StyledButton } from "../../components/StyledComponents";

interface Props {
    addNumberForCheck: (number: string) => void,
    updateBlockingState: React.Dispatch<BlockingState>,
    curr: BlockingState
}

type BlockingState = {
    number: string;
    is_outbound: boolean;
} | null;

export const NumberBlockContext = createContext<Props | null>(null)

export const useNumberBlockContext = () => {
    const context = useContext(NumberBlockContext)

    if (!context?.addNumberForCheck) {
        throw new Error(
            'Component requiring addNumberForReq rendered outside NumberBlockProvider'
        )
    }

    return context;
}

const NumberBlockingForm: React.FC = () => {
    const { curr, updateBlockingState } = useNumberBlockContext();

    const voip_user_uuid = useTypedSelector(selectCurrentUserId);

    const [addBlockList, { isLoading }] = contactsApi.endpoints.addNumberBlock.useMutation();

    return (
        <ModalFormContainer
            showModal={!!curr}
            setShowModal={() => updateBlockingState(null)}
        >
            {curr ? (
                <div
                    className={[formStyles.formContainer, styles.call_blocking_module].join(' ')}
                >
                    <h2>
                        Block this number?
                    </h2>
                    <span>
                        This will block {curr.is_outbound ? 'outbound' : 'inbound'} calls {curr.is_outbound ? 'from' : 'to'} this number across your account.
                    </span>
                    <div>
                        <StyledButton
                            buttonStyle='tertiary'
                            size='large'
                            onClick={() => {
                                updateBlockingState(null)
                            }}
                        >
                            <p>Cancel</p>
                        </StyledButton>
                        <StyledButton
                            buttonStyle='primary'
                            size='large'
                            onClick={() => {
                                addBlockList({
                                    voip_user_uuid,
                                    data: { numbers: [curr.number] },
                                    direction: curr.is_outbound ? 'outbound' : 'inbound'
                                }).then(() => {
                                    updateBlockingState(null)
                                })
                            }}
                            loading={isLoading}
                            disabled={isLoading}
                        >
                            <p>
                                Block {curr.is_outbound ? 'outbound' : 'inbound'} calls
                            </p>
                        </StyledButton>
                    </div>
                </div>
            ) : null}
        </ModalFormContainer>
    );
}

export const NumberBlockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [checkNumberBlocks] = contactsApi.endpoints.checkNumberBlocks.useMutation();

    const numbersArr = useRef<string[]>([]);
    const timer = useRef<NodeJS.Timeout>();
    const checkedNumbers = useRef<Set<string>>(new Set());

    const addNumberForCheck = useCallback((num: string) => {
        if (checkedNumbers.current.has(num)) return;

        checkedNumbers.current.add(num)
        numbersArr.current.push(num);

        clearTimeout(timer.current);

        timer.current = setTimeout(() => {
            checkNumberBlocks({ numbers: numbersArr.current })
            numbersArr.current = [];
        }, 1000)
    }, []);

    useEffect(() => () => {
        clearTimeout(timer.current);
    }, []);

    const handleUpdateBlockingState = useCallback((state: BlockingState, newState: BlockingState): BlockingState => newState, []);

    const [curr, updateBlockingState] = useReducer<Reducer<BlockingState, BlockingState>>(handleUpdateBlockingState, null);

    return (
        <NumberBlockContext.Provider value={{ addNumberForCheck, updateBlockingState, curr }}>
            <NumberBlockingForm />
            {children}
        </NumberBlockContext.Provider>
    )
}

export default NumberBlockProvider
